import { useState, useEffect, useMemo } from 'react';

import { useAtom } from 'jotai';

import { getMMMData, getMMMRuns } from '../../../api/mmmApi';
import { execDtAtom, kpiAtom } from '../../../atoms';
import { unixToReadableDate } from '../helpers';

/**
 * useModelRuns
 *
 * This hook fetches and manages model runs, allowing the user to select a model run by execution date (execDt).
 * It also provides options for selecting models and updates the KPI type based on the selected model.
 *
 * @returns {Object} An object containing:
 *  - `selectedExecDt`: The currently selected execution date.
 *  - `setSelectedExecDt`: A function to update the selected execution date.
 *  - `modelOptions`: An array of model options formatted for use in a dropdown, where each option contains:
 *      - `key`: The execution date of the model.
 *      - `text`: The model name or a human-readable execution date.
 *      - `value`: The execution date.
 *  - `modelRuns`: An array of all fetched model runs.
 */
export const useModelRuns = () => {
  const [selectedExecDt, setSelectedExecDt] = useAtom(execDtAtom);
  const [, setKPI] = useAtom(kpiAtom);
  const [modelRuns, setModelRuns] = useState([]);

  useEffect(() => {
    getMMMRuns()
      .then(data => {
        const sortDataByExecDt = [...data].sort((a, b) => b.exec_dt - a.exec_dt);
        setModelRuns(sortDataByExecDt);
        const currentSelectedModelExists = data.find(x => x.exec_dt === selectedExecDt);
        if (!currentSelectedModelExists) {
          setSelectedExecDt(sortDataByExecDt[0].exec_dt);
        }
      });
  }, []);

  useEffect(() => {
    if (!selectedExecDt && modelRuns.length) {
      setSelectedExecDt(modelRuns[0].exec_dt);
    }
  }, [selectedExecDt, modelRuns]);

  const modelOptions = useMemo(() => modelRuns.map(item => ({
    key: item.exec_dt,
    text: item.name ? item.name : unixToReadableDate(item.exec_dt),
    value: item.exec_dt,
  })), [modelRuns]);

  const selectedModel = useMemo(() => {
    if (!selectedExecDt) return false;
    return modelRuns.find(x => x.exec_dt === selectedExecDt);
  }, [selectedExecDt, modelRuns]);

  useEffect(() => {
    if (selectedModel) {
      const { kpi } = selectedModel;
      setKPI(kpi);
    }
  }, [selectedModel]);

  return {
    selectedExecDt,
    setSelectedExecDt,
    modelOptions,
    modelRuns,
  };
};

/**
 * useCurrentModelRunData
 *
 * This hook fetches the data for a specific model run based on the provided execution date (`execDt`).
 * It ensures that the `execDt` corresponds to a valid model run and retrieves the associated data.
 *
 * @param {string} execDt - The execution date of the model run, in `YYYY-MM-DD` format.
 * @param {Array} modelRuns - An array of model runs, each containing metadata such as the `exec_dt`.
 *
 * @returns {Object} An object containing:
 *  - `data`: The fetched model run data for the given `execDt`, or `false` if an error occurred during the fetch.
 *
 */
export const useCurrentModelRunData = (execDt, modelRuns) => {
  const [data, setData] = useState();
  const validExecDt = modelRuns.find(x => x.exec_dt === execDt);

  useEffect(() => {
    if (!execDt || !validExecDt) return;
    getMMMData(execDt)
      .then(response => setData(response))
      .catch(err => {
        setData(false);
        console.error(`MMM Model Data Exec DT ${execDt} error: ${err}`);
      });
  }, [execDt, validExecDt]);

  return {
    data,
  };
};
